import { Box, Breadcrumbs, Link, Typography } from "@mui/material";
import { FunctionComponent, useCallback, useEffect, useMemo } from "react";
import { AccordionType, StatistiquesContextProvider, useStatistiquesContext, ViewMode } from './statistiquesContext';
import StatistiquePageHeader from './StatistiquesPageHeader';
import StatistiquesGrid from './StatistiquesGrid';
import { StatistiquesFiltersAccordions } from "./StatistiquesFilters";
import { PlanoStatsNiveau } from "../../redux/features/types";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export interface BreadcrumbItem {
    label: string;
    active?: boolean;
    disabled?: boolean;
    onClick?: () => void;
}

export const StatistiquesBreadcrumbs: FunctionComponent = () => {
    const {
        niveau,
        setNiveau,
        selectedDivisions,
        selectedSections,
        selectedTypeProduitWeb,
        setProductAccordionExpanded,
    } = useStatistiquesContext();

    const getIndexFromNiveau = useCallback((niveau: PlanoStatsNiveau): number => {
        switch (niveau) {
            case PlanoStatsNiveau.DIVISION:
                return 0;
            case PlanoStatsNiveau.SECTION:
                return 1;
            case PlanoStatsNiveau.TYPE_PRODUIT_WEB:
                return 2;
            case PlanoStatsNiveau.PRODUIT:
                return 3;
            default:
                return 0;
        }
    }, []);

    const BREADCRUMB_ITEMS: BreadcrumbItem[] = useMemo(() => {
        return [
            {
                label: "Divisions",
                active: getIndexFromNiveau(niveau) === 0,
                // disabled: selectedMagasins.length === 0,
                onClick: () => {
                    setNiveau(PlanoStatsNiveau.DIVISION);
                    setProductAccordionExpanded(AccordionType.DIVISION);
                },
            },
            {
                label: "Sections",
                active: getIndexFromNiveau(niveau) === 1,
                disabled: selectedDivisions.length === 0,
                onClick: () => {
                    setNiveau(PlanoStatsNiveau.SECTION);
                    setProductAccordionExpanded(AccordionType.SECTION);
                },
            },
            {
                label: "Type de produits",
                active: getIndexFromNiveau(niveau) === 2,
                disabled: selectedSections.length === 0,
                onClick: () => {
                    setNiveau(PlanoStatsNiveau.TYPE_PRODUIT_WEB);
                    setProductAccordionExpanded(AccordionType.TYPE_PRODUIT_WEB);
                },
            },
            {
                label: "Produits",
                active: getIndexFromNiveau(niveau) === 3,
                disabled: selectedTypeProduitWeb.length === 0,
                onClick: () => {
                    setNiveau(PlanoStatsNiveau.PRODUIT);

                },
            },
        ];
    }, [niveau, getIndexFromNiveau, setNiveau]);

    return (
        <Box
            sx={{
                paddingLeft: 1,
            }}
        >
            <Breadcrumbs
                aria-label="breadcrumbs"
                separator={<ChevronRightIcon fontSize="medium" />}
                sx={{
                    "& .breadcrumbs__text": {
                        fontSize: "0.875rem",
                        cursor: "pointer",

                        "&:hover": {
                            textDecoration: "underline",
                        },

                        "&.active": {
                            fontWeight: "bold",
                            textDecoration: "underline",
                        },

                        "&.disabled": {
                            color: "text.disabled",
                            cursor: "not-allowed",
                            textDecoration: "none",
                        },
                    },
                }}
            >
                {BREADCRUMB_ITEMS.map((item, index) => {
                    return (
                        <Typography
                            key={index}
                            className={`breadcrumbs__text ${item.active ? "active" : ""} ${item.disabled ? "disabled" : ""}`}
                            color="text.primary"
                            onClick={() => {
                                if (item.disabled) return;
                                item.onClick?.();
                            }}
                        >
                            {item.label}
                        </Typography>
                    );
                })}
            </Breadcrumbs>
        </Box>
    );
};

const StatistiquesView: FunctionComponent = () => {
    const {
        currentView,
    } = useStatistiquesContext();

    return (
        <Box
            display="flex"
            flexDirection="row"
            sx={{
                height: "100%",
                maxHeight: "calc(100% - 70px)",
            }}
        >
            <StatistiquesFiltersAccordions />
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    maxHeight: `calc(100% - ${currentView === ViewMode.PRODUCT ? 54 : 30}px)`,
                    flex: 1,
                    boxSizing: 'border-box',
                    overflow: "hidden",
                }}
            >
                {currentView === ViewMode.PRODUCT && <StatistiquesBreadcrumbs />}
                <StatistiquesGrid />
            </Box>
        </Box>
    )
};


const StatistiquesPage: FunctionComponent = () => {
    return (
        <div
            style={{
                position: "fixed",
                height: "calc(100vh - 60px)",
                overflow: "hidden",
                width: "100%",
            }}
        >
            <StatistiquePageHeader />
            <StatistiquesView />
        </div>
    )
};

const StatistiquesWrapper: FunctionComponent = () => {
    return (
        <StatistiquesContextProvider>
            <StatistiquesPage />
        </StatistiquesContextProvider>
    );
};

export default StatistiquesWrapper;